/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Interactive Prompt Template Card Component (Task 10.2.5 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Heart,
  Copy,
  Check,
  ArrowRight,
} from 'lucide-react';
import { SophiaIcon, type SophiaDefaultIconName } from '@sofia/assets';
import type { EnrichedPromptTemplate } from '../../services/prompt-catalog.service';
import { usePromptTemplateStore } from '../../stores/usePromptTemplateStore';
import { useI18n } from '../../stores/useI18nStore';

export interface PromptTemplateCardProps {
  template: EnrichedPromptTemplate;
  onSelect: (template: EnrichedPromptTemplate) => void;
  compact?: boolean;
}

/**
 * Category styling tokens and standardized Sophia icons.
 */
function getCategoryBadge(categoryId: string): { bg: string; icon: SophiaDefaultIconName } {
  switch (categoryId) {
    case 'pcat_blog':
      return {
        bg: 'bg-primary/10 text-primary border-primary/20',
        icon: 'brainstorm',
      };
    case 'pcat_marketing':
      return {
        bg: 'bg-accent/10 text-accent border-accent/20',
        icon: 'strategy',
      };
    case 'pcat_email':
      return {
        bg: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
        icon: 'virtual-call',
      };
    case 'pcat_ecommerce':
      return {
        bg: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
        icon: 'digital-wallet',
      };
    case 'pcat_coding':
      return {
        bg: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
        icon: 'binary-code',
      };
    default:
      return {
        bg: 'bg-primary/10 text-primary border-primary/20',
        icon: 'solution',
      };
  }
}

/**
 * Model badge styling.
 */
function getModelBadge(model: string) {
  switch (model) {
    case 'claude-3-5-sonnet':
      return { label: 'Claude 3.5 Sonnet', color: 'text-amber-500 bg-amber-500/10 border-amber-500/20' };
    case 'deepseek-r1':
      return { label: 'DeepSeek R1', color: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/20' };
    case 'gemini-1-5-pro':
      return { label: 'Gemini 1.5 Pro', color: 'text-primary bg-primary/10 border-primary/20' };
    case 'gpt-4o':
    default:
      return { label: 'GPT-4o', color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20' };
  }
}

export const PromptTemplateCard: React.FC<PromptTemplateCardProps> = ({
  template,
  onSelect,
  compact = false,
}) => {
  const { isFavorite, toggleFavorite, markRecentlyUsed } = usePromptTemplateStore();
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const favorited = isFavorite(template.slug);

  const categoryBadge = getCategoryBadge(template.categoryId);
  const modelBadge = getModelBadge(template.recommendedModel);
  const inputCount = template.inputSchema?.length ?? 0;

  const handleUseTemplate = () => {
    markRecentlyUsed(template.slug);
    onSelect(template);
  };

  const handleCopyPrompt = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(template.promptTemplate);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(template.slug);
  };

  if (compact) {
    return (
      <div
        onClick={handleUseTemplate}
        className="group relative flex items-center justify-between p-3.5 rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated hover:border-primary/40 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md"
      >
        <div className="flex items-center gap-3.5 min-w-0 pr-4">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${categoryBadge.bg}`}
          >
            <SophiaIcon name={categoryBadge.icon} size={16} className="shrink-0" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                {template.title}
              </h4>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${categoryBadge.bg}`}
              >
                {template.category.name}
              </span>
            </div>
            <p className="text-xs text-foreground-muted truncate max-w-xl mt-0.5">
              {template.description}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <span
            className={`hidden sm:inline-flex text-[10px] font-mono px-2 py-0.5 rounded-full font-semibold border ${modelBadge.color}`}
          >
            {modelBadge.label}
          </span>

          <span className="text-xs font-mono font-medium text-amber-500 inline-flex items-center gap-1">
            <SophiaIcon name="battery" size={12} className="text-amber-500 shrink-0" />
            {template.creditsCost}
          </span>

          <button
            type="button"
            onClick={handleToggleFavorite}
            className={`p-1.5 rounded-lg border transition-colors ${
              favorited
                ? 'bg-rose-500/10 border-rose-500/30 text-rose-500'
                : 'border-surface-border text-foreground-muted hover:text-rose-500'
            }`}
          >
            <Heart
              className={`w-4 h-4 transition-all ${
                favorited ? 'fill-rose-500 scale-110' : ''
              }`}
            />
          </button>

          <button
            type="button"
            onClick={handleUseTemplate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-fg hover:bg-primary-hover shadow-sm transition-all"
          >
            <span>{t('templates.runTemplateBtn')}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // Regular Grid Card
  return (
    <div
      onClick={handleUseTemplate}
      className="group relative flex flex-col justify-between p-5 rounded-2xl border border-surface-border bg-surface hover:bg-surface-elevated hover:border-primary/40 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-glow-primary/10"
    >
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center border shadow-inner ${categoryBadge.bg}`}
          >
            <SophiaIcon name={categoryBadge.icon} size={20} className="shrink-0" />
          </div>

          <div className="flex items-center gap-1.5">
            <span
              className={`text-[10px] font-mono px-2 py-0.5 rounded-full font-semibold border ${modelBadge.color}`}
            >
              {modelBadge.label}
            </span>
            <button
              type="button"
              onClick={handleToggleFavorite}
              title={favorited ? 'Remove favorite' : 'Save favorite'}
              className={`p-1.5 rounded-xl border transition-all ${
                favorited
                  ? 'bg-rose-500/10 border-rose-500/30 text-rose-500'
                  : 'border-surface-border text-foreground-muted hover:text-rose-500 hover:bg-rose-500/10'
              }`}
            >
              <Heart
                className={`w-4 h-4 transition-all ${
                  favorited ? 'fill-rose-500 scale-110' : 'group-hover:scale-110'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Title & Description */}
        <div className="space-y-1.5">
          <h4 className="text-base font-bold text-foreground group-hover:text-primary transition-colors tracking-tight line-clamp-1">
            {template.title}
          </h4>
          <p className="text-xs text-foreground-muted line-clamp-2 leading-relaxed">
            {template.description}
          </p>
        </div>
      </div>

      {/* Meta Pills & Action Footer */}
      <div className="pt-4 mt-4 border-t border-surface-border/60 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <span
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md font-semibold border ${categoryBadge.bg}`}
          >
            {template.category.name}
          </span>

          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] text-foreground-muted">
              <SophiaIcon name="filter" size={12} className="text-foreground-muted/70 shrink-0" />
              {inputCount} fields
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-mono font-semibold px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-500 border border-amber-500/20">
              <SophiaIcon name="battery" size={12} className="text-amber-500 shrink-0" />
              {template.creditsCost} {t('common.credits')}
            </span>
          </div>
        </div>

        {/* Buttons row */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCopyPrompt}
            title={t('common.copy')}
            className="p-2 rounded-xl border border-surface-border text-foreground-muted hover:text-foreground hover:bg-muted/60 transition-colors"
          >
            {copied ? (
              <Check className="w-4 h-4 text-emerald-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>

          <button
            type="button"
            onClick={handleUseTemplate}
            className="flex-1 flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold bg-primary text-primary-fg hover:bg-primary-hover shadow-sm transition-all group-hover:shadow-glow-primary/20"
          >
            <span>{t('templates.runTemplateBtn')}</span>
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
